import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {ConfirmPopup} from "primereact/confirmpopup";
import {Menu} from "primereact/menu";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {useCheckPermissionAdmin} from "../../hooks/permissionHooks";
import StatusAllIcon from "../../images/display_status_all.svg?react";
import StatusDefaultIcon from "../../images/display_status_default.svg?react";
import StatusLimitedIcon from "../../images/display_status_limited.svg?react";
import ElasticIcon from "../../images/elastic_icon.svg?react";
import {selectAppEnvironment} from "../../redux/envSlice";
import {abortRunningIndications} from "../../redux/indicationRequests";
import {selectCurrentIndicationPayload} from "../../redux/indicationSelectors";
import {resetObjectParameters} from "../../redux/objectParametersSlice";
import {validateFormThunk} from "../../redux/objectParametersThunk";
import {selectReadOnly, selectRevaId} from "../../redux/persistenceSlice";
import {clearIndicationData} from "../../redux/valueIndicationSlice";
import {selectHasValueOrders} from "../../redux/valueOrdersSlice";
import {kibanaLink} from "../../util/ibLinks";

// Die Icons zeichnen mit currentColor, erben also die Farbe des Buttons und folgen damit
// dem Theme von selbst — ein zweiter Dateisatz für das dunkle Theme entfällt.
const DISPLAY_STATUS_ICONS = {
    default: StatusDefaultIcon,
    all: StatusAllIcon,
    required: StatusLimitedIcon,
};

function ElasticSearchIcon() {
    return <ElasticIcon aria-hidden="true" width="20" height="20" />;
}

/**
 * @param {ObjectDisplayStatus} displayStatus
 * @param {(status: ObjectDisplayStatus) => void} setDisplayStatus
 */
function ObjectOverviewToolbar({displayStatus, setDisplayStatus}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const toastRef = useRef(null);
    const [confirmResetVisible, setConfirmResetVisible] = useState(false);
    const resetButton = useRef(null);
    const debugObj = useSelector(selectCurrentIndicationPayload);
    const debugJson = JSON.stringify(debugObj, null, 4);
    const readOnly = useSelector(selectReadOnly);
    const revaId = useSelector(selectRevaId);
    const elasticMenuRef = useRef(null);

    const isAdmin = useCheckPermissionAdmin();
    const hasValueOrders = useSelector(selectHasValueOrders);
    const env = useSelector(selectAppEnvironment);

    const onReset = () => {
        // Laufende Indikation zum alten Parametersatz abbrechen (#19374)
        abortRunningIndications();
        dispatch(resetObjectParameters());
        dispatch(clearIndicationData());
        dispatch(validateFormThunk());
    };

    const onJsonCopy = async () => {
        try {
            await navigator.clipboard.writeText(debugJson);
            toastRef.current.show({
                severity: "info",
                summary: "Info",
                detail: t("object.copiedJsonToClipboard"),
            });
        } catch (e) {
            console.warn("Could not copy JSON to clipboard", e);
        }
    };

    const onRevaIdCopy = async () => {
        try {
            await navigator.clipboard.writeText(revaId);
            toastRef.current.show({
                severity: "info",
                summary: "Info",
                detail: t("object.copiedRevaIdToClipboard"),
            });
        } catch (e) {
            console.warn("Could not copy REVA ID to clipboard", e);
        }
    };

    /**@type {ObjectDisplayStatus[]} */
    const displayOptions = ["default", "all", "required"];

    const getTooltip = (option) => {
        switch (option) {
            case "all":
                return t("object.actions.displayAll");
            case "required":
                return t("object.actions.displayRequired");
            default:
                return t("object.actions.displayDefault");
        }
    };

    const getStateChangeToastMessage = (option) => {
        switch (option) {
            case "all":
                return t("object.actions.stateChangedToAll");
            case "required":
                return t("object.actions.stateChangedToRequired");
            default:
                return t("object.actions.stateChangedDefault");
        }
    };

    const DisplayStatusIcon = DISPLAY_STATUS_ICONS[displayStatus];

    const cycleDisplayStatus = () => {
        const currentIndex = displayOptions.indexOf(displayStatus);
        const nextStatus = displayOptions[(currentIndex + 1) % displayOptions.length];

        setDisplayStatus(nextStatus);

        toastRef.current.replace([
            {
                severity: "info",
                summary: "Info",
                detail: getStateChangeToastMessage(nextStatus),
            },
        ]);
    };

    const onElasticMenuToggle = (event) => {
        if (elasticMenuRef.current) {
            elasticMenuRef.current.toggle(event);
        }
    };

    const elasticLinkItems = [
        {
            label: "ElasticSearch / Kibana Links",
            items: [
                {
                    label: "REVA-Bewertung",
                    icon: <ElasticSearchIcon />,
                    url: kibanaLink({
                        role: env.role,
                        dataViewAlias: "reva-stored-valuations",
                        query: `revaId: "${revaId}"`,
                        from: "now-1y%2Fd",
                        sortBy: "metadata.lastUpdateDate",
                    }),
                    target: "_blank",
                },
                {
                    label: "Erstbewertung bei Kreditvergabe",
                    icon: <ElasticSearchIcon />,
                    url: kibanaLink({
                        role: env.role,
                        dataViewAlias: "reva-initial-valuations",
                        query: `revaId: "${revaId}"`,
                        from: "now-1y%2Fd",
                        sortBy: "dateImported",
                    }),
                    target: "_blank",
                    visible: hasValueOrders,
                },
                {
                    label: "Besichtigungsdaten der Value AG",
                    icon: <ElasticSearchIcon />,
                    url: kibanaLink({
                        role: env.role,
                        dataViewAlias: "reva-inspections",
                        query: `revaId: "${revaId}"`,
                        from: "now-1y%2Fd",
                        sortBy: "dateImported",
                    }),
                    target: "_blank",
                    visible: hasValueOrders,
                },
                {
                    label: "REVA Nutzungs-Log",
                    icon: <ElasticSearchIcon />,
                    url: kibanaLink({
                        role: env.role,
                        dataViewAlias: "usage-reva-api",
                        query: `details.valuation.revaId: "${revaId}"`,
                        from: "now-1y%2Fd",
                        sortBy: "dateTime",
                    }),
                    target: "_blank",
                },
                {
                    label: "AVM Nutzungs-Log",
                    icon: <ElasticSearchIcon />,
                    url: kibanaLink({
                        role: "ALWAYS_HUMBOLDT",
                        dataViewAlias: "usage-avm-api",
                        query: `details.revaId: "${revaId}"`,
                        from: "now-1y%2Fd",
                        sortBy: "dateTime",
                    }),
                    target: "_blank",
                },
            ],
        },
    ];

    return (
        <div className="HeaderToolbarRight">
            {isAdmin && (
                <>
                    <Menu
                        id="elasticMenu"
                        ref={elasticMenuRef}
                        model={elasticLinkItems}
                        popup
                        popupAlignment="right"
                        style={{width: "21rem"}}
                        pt={{label: {className: "ml-2"}}}
                    />
                    <Button
                        id="elasticLinks"
                        icon={<ElasticSearchIcon />}
                        aria-label="ElasticSearch / Kibana Links"
                        severity="secondary"
                        onClick={onElasticMenuToggle}
                        text
                    />
                </>
            )}
            <Tooltip target="#copyRevaIdInfo" position="left" autoHide="false">
                <div>{t("object.actions.revaId")}</div>
                <pre>{revaId}</pre>
                <div>
                    <i>{t("object.actions.copyToClipboard")}</i>
                </div>
            </Tooltip>
            <Button id="copyRevaIdInfo" icon={PrimeIcons.HASHTAG} severity="secondary" onClick={onRevaIdCopy} text />
            <Tooltip target="#valueJsonInfo" position="left" autoHide="false">
                <div>{t("object.actions.avmJsonRequest")}</div>
                <pre>{debugJson}</pre>
                <div>
                    <i>{t("object.actions.copyToClipboard")}</i>
                </div>
            </Tooltip>
            <Button id="valueJsonInfo" icon={PrimeIcons.CODE} severity="secondary" onClick={onJsonCopy} text />
            <Button
                id="displayStatusToggle"
                className="ml-1"
                severity="secondary"
                text
                icon={<DisplayStatusIcon aria-hidden="true" width="20" height="20" />}
                aria-label={getTooltip(displayStatus)}
                onClick={cycleDisplayStatus}
                tooltip={getTooltip(displayStatus)}
                tooltipOptions={{position: "top"}}
            />
            <ConfirmPopup
                target={resetButton.current}
                visible={confirmResetVisible}
                icon={PrimeIcons.EXCLAMATION_TRIANGLE}
                onHide={() => setConfirmResetVisible(false)}
                accept={onReset}
                message={t("object.actions.confirmReset")}
                acceptClassName="p-button-danger"
                acceptLabel={t("general.confirm.yes")}
                rejectLabel={t("general.confirm.no")}
            />
            <Button
                icon={PrimeIcons.TRASH}
                tooltip={t("object.actions.reset")}
                text
                tooltipOptions={{position: "left"}}
                className="ml-1"
                severity="danger"
                onClick={() => setConfirmResetVisible(true)}
                disabled={readOnly}
                ref={resetButton}
                aria-label={"Reset"}
            />

            <Toast ref={toastRef} position="top-center" />
        </div>
    );
}

export default ObjectOverviewToolbar;
